// Recipes Amari wrote on request (create-recipe), kept on this device.
//
// They join the collection for this person only: getRecipe, steps, photos, recommendations, saved
// recipes and plans all see them (recipes.ts, recipeMeta.ts, recipeImages.ts and recommend.ts read
// from here). Each is checked with the collection's own schema before it's kept, uses taxonomy
// ingredients, so the rule engine judges it like any other recipe, and carries its unlisted
// ingredients ("extras") with allergen and diet flags, which can rule it out too.
//
// Loaded synchronously at startup, before the saved state is read, so saved and planned ids that
// point at these recipes stay valid.

import { entrySchema } from './catalog/schema';
import { toShipped } from './catalog/ship';
import type { DietRuleId } from './diet';
import type { RecipeStep, ShippedRecipe } from './recipes';

export interface Extra {
  name: string;
  qty: string;
  role: 'core' | 'pantry' | 'optional';
  allergens: string[];
  meat: 'none' | 'poultry' | 'beef' | 'pork' | 'lamb' | 'other';
  seafood: boolean;
  animal: boolean;
  gluten: boolean;
  alcohol: boolean;
}

export interface Source { title: string; url: string }

export interface GeneratedRecipe {
  recipe: ShippedRecipe;
  steps: RecipeStep[];
  extras: Extra[];
  sources: Source[];
  imageUrl: string | null;
  imageStatus: 'pending' | 'ready' | 'failed';
  /** The automatic review it passed on the server (food safety, allergens, method), if known. */
  review: { revised: boolean; summary: string } | null;
  savedAt: string;
}

const KEY = 'amari_generated';
const MAX = 60;
const ROLES = ['core', 'pantry', 'optional'];
const MEATS = ['none', 'poultry', 'beef', 'pork', 'lamb', 'other'];

let byId = new Map<string, GeneratedRecipe>();
let version = 0;
const listeners = new Set<() => void>();

const isHttps = (u: unknown): u is string => typeof u === 'string' && /^https:\/\/[^\s]+$/.test(u) && u.length < 600;
/** Photos come from this project's Supabase Storage only. */
const isPhotoUrl = (u: unknown): u is string => isHttps(u) && /^https:\/\/[a-z0-9-]+\.supabase\.co\/storage\/v1\/object\/public\/amari-recipes\//.test(u);

function toExtra(x: unknown): Extra | null {
  const e = (x ?? {}) as Record<string, unknown>;
  if (typeof e.name !== 'string' || !e.name.trim()) return null;
  return {
    name: e.name.trim().slice(0, 40),
    qty: typeof e.qty === 'string' ? e.qty.slice(0, 30) : '',
    role: ROLES.includes(e.role as string) ? e.role as Extra['role'] : 'core',
    allergens: Array.isArray(e.allergens) ? e.allergens.filter((a): a is string => typeof a === 'string').slice(0, 9) : [],
    meat: MEATS.includes(e.meat as string) ? e.meat as Extra['meat'] : 'other',
    seafood: e.seafood === true,
    // Missing answers are treated as "could be", the cautious reading.
    animal: e.animal !== false,
    gluten: e.gluten !== false,
    alcohol: e.alcohol === true,
  };
}

/**
 * A recipe from the server (or this device's copy), or null if anything about it is off: it must
 * pass the collection's schema, be one of Amari's ids, and be an unreviewed draft.
 */
export function toGenerated(raw: {
  recipe: unknown; extras?: unknown; sources?: unknown; image_url?: unknown; image_status?: unknown; review?: unknown; savedAt?: unknown;
}): GeneratedRecipe | null {
  const parsed = entrySchema.safeParse(raw.recipe);
  if (!parsed.success) return null;
  const entry = parsed.data;
  if (!/^amari-[a-z0-9-]{1,80}$/.test(entry.id) || entry.review.status !== 'draft' || entry.tested) return null;
  const extras = (Array.isArray(raw.extras) ? raw.extras : []).map(toExtra).filter((e): e is Extra => e !== null).slice(0, 5);
  const sources = (Array.isArray(raw.sources) ? raw.sources : [])
    .filter((s): s is Source => !!s && typeof (s as Source).title === 'string' && isHttps((s as Source).url))
    .map((s) => ({ title: s.title.slice(0, 80), url: s.url }))
    .slice(0, 4);
  const status = raw.image_status === 'ready' || raw.image_status === 'failed' ? raw.image_status : 'pending';
  const imageUrl = isPhotoUrl(raw.image_url) ? raw.image_url : null;
  return {
    recipe: toShipped(entry) as unknown as ShippedRecipe,
    steps: entry.steps,
    extras,
    sources,
    imageUrl,
    imageStatus: imageUrl ? 'ready' : status === 'ready' ? 'failed' : status,
    review: toReview(raw.review),
    savedAt: typeof raw.savedAt === 'string' ? raw.savedAt : new Date().toISOString(),
  };
}

function toReview(v: unknown): GeneratedRecipe['review'] {
  const r = v as { passed?: unknown; revised?: unknown; summary?: unknown } | null | undefined;
  return r && r.passed === true ? { revised: r.revised === true, summary: typeof r.summary === 'string' ? r.summary.slice(0, 300) : '' } : null;
}

function serialise(g: GeneratedRecipe) {
  const { stepCount: _count, ...rest } = g.recipe as ShippedRecipe & { stepCount: number };
  return {
    recipe: { ...rest, steps: g.steps }, extras: g.extras, sources: g.sources, image_url: g.imageUrl, image_status: g.imageStatus,
    review: g.review ? { passed: true, ...g.review } : null, savedAt: g.savedAt,
  };
}

function save() {
  try { localStorage.setItem(KEY, JSON.stringify([...byId.values()].map(serialise))); } catch { /* full or private mode */ }
}

function changed() {
  version++;
  save();
  listeners.forEach((l) => l());
}

function load() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? '[]') as unknown;
    if (!Array.isArray(raw)) return;
    for (const r of raw.slice(-MAX)) {
      const g = toGenerated(r as Parameters<typeof toGenerated>[0]);
      if (g) byId.set(g.recipe.id, g);
    }
  } catch { /* nothing saved, or unreadable: start empty */ }
}
load();

export function generatedRecipe(id: string): GeneratedRecipe | undefined {
  return byId.get(id);
}

export function generatedRecipes(): GeneratedRecipe[] {
  return [...byId.values()];
}

export function isGenerated(id: string): boolean {
  return byId.has(id);
}

/** Keeps a new recipe (the newest 60 are kept). */
export function addGenerated(g: GeneratedRecipe): void {
  byId.delete(g.recipe.id);
  byId.set(g.recipe.id, g);
  if (byId.size > MAX) byId = new Map([...byId].slice(-MAX));
  changed();
}

export function setGeneratedPhoto(id: string, imageUrl: unknown, status: unknown): void {
  const g = byId.get(id);
  if (!g) return;
  const url = isPhotoUrl(imageUrl) ? imageUrl : null;
  const next = url ? 'ready' : status === 'failed' ? 'failed' : 'pending';
  if (g.imageUrl === url && g.imageStatus === next) return;
  byId.set(id, { ...g, imageUrl: url, imageStatus: next });
  changed();
}

export function subscribeGenerated(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Changes whenever a recipe is added or gets its photo, for React (useSyncExternalStore). */
export function generatedVersion(): number {
  return version;
}

/** For tests: forget everything. */
export function resetGenerated(): void {
  byId = new Map();
  changed();
}

/**
 * The first unlisted ingredient that breaks one of the person's rules, judged from the flags the
 * writer gave it. Recipes with one are left out for this person, like any recipe that breaks a rule.
 */
export function extraBreaksRule(extras: Extra[], rules: DietRuleId[]): { extra: Extra; rule: DietRuleId } | null {
  for (const e of extras) {
    if (e.role === 'optional') continue;
    for (const rule of rules) {
      const allergy = rule.startsWith('allergy_') ? rule.slice('allergy_'.length) : null;
      const meat = e.meat !== 'none';
      const breaks = allergy ? e.allergens.includes(allergy) || (allergy === 'wheat' && e.gluten && e.allergens.length === 0)
        : rule === 'vegetarian' ? meat || e.seafood
        : rule === 'vegan' ? meat || e.seafood || e.animal
        : rule === 'pescatarian' ? meat
        : rule === 'gluten_free' ? e.gluten
        : rule === 'no_pork' ? e.meat === 'pork' || e.meat === 'other'
        : rule === 'no_beef' ? e.meat === 'beef' || e.meat === 'other'
        : rule === 'halal' ? e.meat === 'pork' || e.alcohol
        : false;
      if (breaks) return { extra: e, rule };
    }
  }
  return null;
}
